import { Layout, Loader, SeoHead } from '@components/common'
import getGlobalStaticProps from '@lib/globalStaticProps'
import {
  ARTICLE_LISTING_PAGE_NAVIGATION_HANDLE,
  GENERAL_PAGE_SIDE_NAVIGATION_HANDLE,
} from '@lib/helpers/general'
import { SliceZone } from '@prismicio/react'
import { components } from '@slices/index'
import type { GetStaticPropsContext, InferGetStaticPropsType } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { createClient, getAllDocByType, getDocByUid } from 'prismicio'

export async function getStaticProps({
  params,
  locale,
  previewData,
}: GetStaticPropsContext) {
  const client = createClient({ previewData })
  const globalProps = await getGlobalStaticProps(client, locale)
  const translations = await serverSideTranslations(locale ?? 'en-au', [
    'common',
  ])
  const page = await getDocByUid(
    'blog-landing-page',
    'the-alchemist',
    client,
    locale,
    [
      'article.name',
      'article.title',
      'article.blogCategory',
      'article.thumbnail',
    ]
  )
  const allArticles = await getAllDocByType('article', client, locale)
  const articleCategories = [
    ...new Set(allArticles?.map((article: any) => article?.data?.blogCategory)),
  ]
  const topNav = await getDocByUid(
    GENERAL_PAGE_SIDE_NAVIGATION_HANDLE,
    ARTICLE_LISTING_PAGE_NAVIGATION_HANDLE,
    client,
    locale
  )
  return {
    props: {
      ...globalProps,
      ...translations,
      page,
      articleCategories,
      topNav: topNav?.data || null,
    },
    // revalidate: 200,
  }
}
export default function TheAlchemist({
  page,
  articleCategories,
  topNav,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const router = useRouter()
  const { data }: any = page || {}
  const { t } = useTranslation('common')

  if (router.isFallback) return <Loader title="product-loader" />
  const title = data?.title
  const description = data?.description
  const { seoImage, seoDescription, seoTitle } = data || {}

  const normalizedCategories: any[] = (articleCategories || []).map(
    (category: string) => t(category?.toLowerCase())
  )
  const { Links }: any = topNav || []

  const navLinks = Links?.filter((link: any) => {
    return normalizedCategories.includes(link.linkTitle)
  })

  return (
    <div className="pb-12 lg:pb-20">
      <SeoHead
        title={seoTitle || title}
        description={seoDescription || description}
        image={seoImage}
      />
      <div className="bg-brand-light-grey">
        <div className="wrapper mb-8 flex flex-col items-center justify-center gap-4 pt-10 text-brand-dark-grey lg:mb-10 lg:items-start lg:pt-20">
          {title && (
            <h1 className="text-mobileh1 font-bold opacity-50 lg:text-h1">
              {title}
            </h1>
          )}
          <div className="mb-1 w-full lg:flex lg:justify-between">
            {description && (
              <div className="body-1 text-center lg:max-w-[30%] lg:text-left xl:max-w-[45%]">
                {description}
              </div>
            )}
            <ul className="body-1-bold flex flex-wrap items-center justify-center gap-x-5 gap-y-2 pt-6 sm:gap-x-10 sm:gap-y-4 lg:gap-8 lg:pt-0">
              {navLinks.map((item: any, index: number) => {
                return (
                  <li
                    key={index}
                    className="cursor-pointer decoration-brand-dark-grey decoration-1 underline-offset-4 hover:underline"
                  >
                    <Link href={item?.link || '/'}>{item?.linkTitle}</Link>
                  </li>
                )
              })}
            </ul>
          </div>
        </div>
        <SliceZone slices={data?.slices} components={components} />
      </div>
    </div>
  )
}

TheAlchemist.Layout = Layout
